import { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Upload, File, X, Check, AlertCircle, Loader2 } from 'lucide-react';
import { upload } from '../../Constant/Constant';
import { HitApiFormData } from '../../Api/ApiHit';
import { setApiJson } from '../../Redux/Actions/ApiAction';

function FileUpload({ title, name, allowed = [], disabled, important = false, error: validationError = false, multiple = false }) {
    const dispatch = useDispatch();
    const ApiReducer = useSelector(state => state.ApiReducer);

    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const fileInputRef = useRef(null);

    // Get current file URL from Redux if exists
    const apiJsonFileUrl = ApiReducer?.apiJson?.[name];

    // For multiple mode: normalize value to array (handles old string records as [string])
    const existingUrls = multiple
        ? (Array.isArray(apiJsonFileUrl) ? apiJsonFileUrl : (apiJsonFileUrl ? [apiJsonFileUrl] : []))
        : [];

    // Helper function to extract filename from URL
    const getFileNameFromUrl = (url) => {
        if (!url) return null;
        try {
            const urlParts = url.split('/');
            return urlParts[urlParts.length - 1] || null;
        } catch (error) {
            return null;
        }
    };

    // Convert allowed types to proper mime types and extensions
    const getAllowedTypes = () => {
        if (!allowed || allowed.length === 0) return null;

        const extensionMap = {
            'pdf': '.pdf',
            'img': '.jpg,.jpeg,.png,.gif,.bmp,.webp,.svg',
            'image': '.jpg,.jpeg,.png,.gif,.bmp,.webp,.svg',
            'video': '.mp4,.avi,.mov,.mkv,.webm',
            'audio': '.mp3,.wav,.ogg',
            'doc': '.doc,.docx',
            'excel': '.xls,.xlsx'
        };

        return allowed
            .map(type => extensionMap[type.toLowerCase()] || `.${type}`)
            .join(',');
    };

    const isFileTypeAllowed = (file) => {
        if (!allowed || allowed.length === 0) return true;

        const fileName = file.name.toLowerCase();
        const fileType = file.type.toLowerCase();

        return allowed.some(allowedType => {
            const type = allowedType.toLowerCase();

            // Check by mime type first (more reliable)
            if (type === 'pdf' && fileType.includes('pdf')) return true;
            if ((type === 'img' || type === 'image') && fileType.startsWith('image/')) return true;
            if (type === 'video' && fileType.startsWith('video/')) return true;
            if (type === 'audio' && fileType.startsWith('audio/')) return true;
            if (type === 'doc' && (fileType.includes('word') || fileType.includes('document'))) return true;
            if (type === 'excel' && (fileType.includes('excel') || fileType.includes('spreadsheet'))) return true;

            // Check by extension for common image formats when type is 'img'
            if (type === 'img' || type === 'image') {
                const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
                if (imageExtensions.some(ext => fileName.endsWith(ext))) return true;
            }

            // Check by extension for other specific types
            if (type === 'pdf' && fileName.endsWith('.pdf')) return true;
            if (type === 'doc' && (fileName.endsWith('.doc') || fileName.endsWith('.docx'))) return true;
            if (type === 'excel' && (fileName.endsWith('.xls') || fileName.endsWith('.xlsx'))) return true;

            // For other types, check if extension matches
            if (fileName.endsWith(`.${type}`)) return true;

            return false;
        });
    };

    // Check if file is an image
    const isImageFile = (file) => {
        if (file?.type) {
            return file.type.startsWith('image/');
        }
        if (file?.name) {
            const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
            return imageExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
        }
        if (file?.url || typeof file === 'string') {
            // If it's a URL, check the extension
            const url = file?.url || file;
            const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
            return imageExtensions.some(ext => url.toLowerCase().includes(ext));
        }
        return false;
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        if (!disabled) {
            setIsDragOver(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (!disabled) {
            const files = Array.from(e.dataTransfer.files);
            if (files.length > 0) {
                if (multiple) {
                    handleMultipleFiles(files);
                } else {
                    handleFile(files[0]);
                }
            }
        }
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            if (multiple) {
                handleMultipleFiles(files);
            } else {
                handleFile(files[0]);
            }
        }
        // reset input so same file can be re-selected later
        if (e.target) e.target.value = '';
    };

    const handleMultipleFiles = async (files) => {
        setUploadError(null);
        const invalid = files.filter(f => !isFileTypeAllowed(f));
        if (invalid.length > 0) {
            setUploadError(`Some files have disallowed type. Allowed: ${allowed.join(', ')}`);
            return;
        }

        setIsUploading(true);
        try {
            const results = await Promise.all(files.map(uploadSingleFile));
            const newUrls = results.filter(r => r?.url).map(r => r.url);
            const failedCount = results.length - newUrls.length;

            if (newUrls.length > 0) {
                const oldJson = ApiReducer?.apiJson || {};
                const currentValue = oldJson[name];
                const currentArr = Array.isArray(currentValue)
                    ? currentValue
                    : (currentValue ? [currentValue] : []);
                dispatch(setApiJson({
                    ...oldJson,
                    [name]: [...currentArr, ...newUrls]
                }));
            }

            if (failedCount > 0) {
                setUploadError(`${failedCount} file(s) failed to upload`);
            }
        } catch (error) {
            setUploadError('Failed to upload files. Please try again.');
            console.error('Upload error:', error);
        } finally {
            setIsUploading(false);
        }
    };

    const handleFile = async (file) => {
        // Check if file type is allowed
        if (!isFileTypeAllowed(file)) {
            setUploadError(`File type not allowed. Allowed types: ${allowed.join(', ')}`);
            return;
        }

        setIsUploading(true);
        setUploadError(null);

        try {
            const result = await uploadSingleFile(file);

            if (result?.url) {
                const fileInfo = {
                    name: file.name,
                    size: formatFileSize(file.size),
                    type: file.type,
                    uploadedAt: new Date().toISOString()
                };

                setUploadedFile({
                    ...fileInfo,
                    url: result.url,
                    status: 'uploaded'
                });

                // Save URL to Redux apiJson
                const oldJson = ApiReducer?.apiJson || {};
                const newJson = {
                    ...oldJson,
                    [name]: result.url
                };
                dispatch(setApiJson(newJson));

            } else {
                setUploadError(result?.error || 'Upload failed');
                setUploadedFile({
                    name: file.name,
                    size: formatFileSize(file.size),
                    type: file.type,
                    status: 'failed',
                    error: result?.error || 'Upload failed'
                });
            }

        } catch (error) {
            setUploadError('Failed to upload file. Please try again.');
            console.error('Upload error:', error);
        } finally {
            setIsUploading(false);
        }
    };

    const uploadSingleFile = async (file) => {
        try {
            // Prepare upload data
            const uploadData = {
                file: file
            };

            // Call HitApiFormData
            const response = await HitApiFormData(uploadData, upload, 'POST');

            console.log("Upload response:", response);

            // Check if upload was successful
            if (response?.statusCode === 200 || response?.statusCode === 201) {
                return response?.data;
            } else {
                return { error: response?.message || response?.error || 'Upload failed' };
            }

        } catch (error) {
            console.error('File upload error:', error);
            return { error: error.message || 'Upload failed' };
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const removeFile = () => {
        setUploadedFile(null);
        setUploadError(null);

        // Clear from Redux apiJson
        const oldJson = ApiReducer?.apiJson || {};
        const newJson = { ...oldJson };
        newJson[name] = "";
        dispatch(setApiJson(newJson));
    };

    const removeUrlAt = (index) => {
        const updated = existingUrls.filter((_, i) => i !== index);
        const oldJson = ApiReducer?.apiJson || {};
        dispatch(setApiJson({
            ...oldJson,
            [name]: updated
        }));
    };

    const openFileDialog = () => {
        if (!disabled) {
            fileInputRef.current?.click();
        }
    };

    const displayFile = !multiple && (uploadedFile ||
        (apiJsonFileUrl ? {
            url: apiJsonFileUrl,
            status: 'uploaded',
            name: getFileNameFromUrl(apiJsonFileUrl) || 'Existing File'
        } : null));

    // Multiple mode: render uploaded list + a small dropzone to add more
    if (multiple) {
        return (
            <div className="w-full">
                {title && (
                    <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700">
                            {title}
                            {important && <span className="text-red-500 ml-1">*</span>}
                        </label>
                    </div>
                )}

                {uploadError && (
                    <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                        <AlertCircle className="w-4 h-4 text-red-500" />
                        <span className="text-red-700 text-sm">{uploadError}</span>
                    </div>
                )}

                {existingUrls.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-3">
                        {existingUrls.map((url, idx) => {
                            const fileName = getFileNameFromUrl(url) || 'File';
                            const isImg = isImageFile({ url });
                            return (
                                <div key={`${url}-${idx}`} className="relative border border-gray-200 rounded-lg overflow-hidden bg-white">
                                    {isImg ? (
                                        <a href={url} target="_blank" rel="noopener noreferrer">
                                            <img
                                                src={url}
                                                alt={fileName}
                                                className="w-full h-24 object-cover"
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        </a>
                                    ) : (
                                        <a
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center justify-center h-24 bg-gray-50"
                                        >
                                            <File className="w-8 h-8 text-gray-500" />
                                        </a>
                                    )}
                                    <div className="px-2 py-1 text-xs text-gray-600 truncate" title={fileName}>
                                        {fileName}
                                    </div>
                                    {!disabled && (
                                        <button
                                            type="button"
                                            onClick={() => removeUrlAt(idx)}
                                            className="absolute top-1 right-1 p-1 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full shadow-sm transition-all"
                                        >
                                            <X className="w-3.5 h-3.5 text-gray-600 hover:text-red-500" />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                <div
                    className={`
                        relative border-2 border-dashed rounded-lg p-4 text-center transition-all duration-300
                        ${isDragOver
                            ? 'border-blue-500 bg-blue-50'
                            : validationError
                                ? 'border-red-300 bg-red-50'
                                : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
                        }
                        ${isUploading || disabled ? 'pointer-events-none opacity-70' : 'cursor-pointer'}
                    `}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={openFileDialog}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                        accept={getAllowedTypes()}
                        disabled={disabled}
                    />
                    <div className="flex flex-col items-center space-y-2">
                        <div className={`p-2 rounded-full ${isDragOver ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                            {isUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">
                                <span className="font-medium">
                                    {isUploading
                                        ? 'Uploading...'
                                        : (existingUrls.length > 0 ? 'Add more files' : 'Drag & Drop files')}
                                </span>
                                {!isUploading && (
                                    <>
                                        {' '}or{' '}
                                        <span className="text-blue-500 font-medium underline">Browse</span>
                                    </>
                                )}
                            </p>
                            {allowed && allowed.length > 0 && (
                                <p className="text-xs text-gray-400 mt-1">
                                    Allowed: {allowed.join(', ')} (multiple)
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {validationError && (
                    <p className="mt-1 text-sm text-red-500">{ApiReducer.apiJsonError[name]}</p>
                )}
            </div>
        );
    }

    return (
        <div className="w-full">
            {title && (
                <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                        {title}
                        {important && <span className="text-red-500 ml-1">*</span>}
                    </label>
                </div>
            )}

            {uploadError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <span className="text-red-700 text-sm">{uploadError}</span>
                </div>
            )}

            {!displayFile && (
                <div
                    className={`
                        relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-300
                        ${isDragOver
                            ? 'border-blue-500 bg-blue-50'
                            : validationError
                                ? 'border-red-300 bg-red-50'
                                : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
                        }
                        ${isUploading || disabled ? 'pointer-events-none opacity-70' : 'cursor-pointer'}
                    `}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={openFileDialog}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileSelect}
                        className="hidden"
                        accept={getAllowedTypes()}
                        disabled={disabled}
                    />

                    <div className="flex flex-col items-center space-y-3">
                        <div className={`
                            p-3 rounded-full transition-colors duration-300
                            ${isDragOver ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'}
                        `}>
                            {isUploading ? (
                                <Loader2 className="w-6 h-6 animate-spin" />
                            ) : (
                                <Upload className="w-6 h-6" />
                            )}
                        </div>

                        <div>
                            <p className="text-sm text-gray-600 mb-1">
                                <span className="font-medium">{isUploading ? 'Uploading...' : 'Drag & Drop your file'}</span>
                                {!isUploading && (
                                    <>
                                        {' '}or{' '}
                                        <span className="text-blue-500 font-medium underline cursor-pointer">
                                            Browse
                                        </span>
                                    </>
                                )}
                            </p>
                            {allowed && allowed.length > 0 && (
                                <p className="text-xs text-gray-400">
                                    Allowed: {allowed.join(', ')}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {displayFile && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Image Preview Section */}
                    {isImageFile(displayFile) && displayFile.url && (
                        <div className="relative">
                            <img
                                src={displayFile.url}
                                alt={displayFile.name || 'Uploaded image'}
                                className="w-full h-48 object-cover"
                                onError={(e) => {
                                    // Hide image if it fails to load
                                    e.target.style.display = 'none';
                                }}
                            />
                            {/* Delete button overlay for images */}
                            {!disabled && (
                                <button
                                    onClick={removeFile}
                                    className="absolute top-2 right-2 p-1 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full shadow-sm transition-all"
                                >
                                    <X className="w-4 h-4 text-gray-600 hover:text-red-500" />
                                </button>
                            )}
                        </div>
                    )}

                    <div className="flex items-center justify-between p-4 bg-white">
                        <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-full ${displayFile.status === 'uploaded' ? 'bg-green-100' : 'bg-red-100'
                                }`}>
                                <File className={`w-4 h-4 ${displayFile.status === 'uploaded' ? 'text-green-600' : 'text-red-600'
                                    }`} />
                            </div>

                            <div>
                                <p className="font-medium text-gray-800 text-sm">
                                    {displayFile.name || 'Uploaded File'}
                                </p>
                                {displayFile.size && (
                                    <p className="text-xs text-gray-500">{displayFile.size}</p>
                                )}
                                {displayFile.error && (
                                    <p className="text-xs text-red-500">{displayFile.error}</p>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center space-x-2">
                            <div className={`flex items-center space-x-1 ${displayFile.status === 'uploaded' ? 'text-green-600' : 'text-red-600'
                                }`}>
                                {displayFile.status === 'uploaded' ? (
                                    <Check className="w-4 h-4" />
                                ) : (
                                    <AlertCircle className="w-4 h-4" />
                                )}
                            </div>

                            {(!isImageFile(displayFile) || !displayFile.url) && !disabled && (
                                <button
                                    onClick={removeFile}
                                    className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                    <X className="w-4 h-4 text-gray-400 hover:text-red-500" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {validationError && (
                <p className="mt-1 text-sm text-red-500">{ApiReducer.apiJsonError[name]}</p>
            )}
        </div>
    );
}

export default FileUpload;
